import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle,
  XCircle,
  ArrowLeft,
  Search,
  RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function MeetingFinder({ user }) {
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const findTimes = async () => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/calendars/find-meeting-time`, {
        date,
        duration_minutes: duration
      }, { withCredentials: true });
      
      setResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to find times");
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityColor = (percentage) => {
    if (percentage >= 100) return "bg-green-500";
    if (percentage >= 75) return "bg-emerald-500";
    if (percentage >= 50) return "bg-yellow-500";
    if (percentage >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  const getAvailabilityBg = (percentage) => {
    if (percentage >= 100) return "bg-green-500/20 border-green-500/30";
    if (percentage >= 75) return "bg-emerald-500/20 border-emerald-500/30";
    if (percentage >= 50) return "bg-yellow-500/20 border-yellow-500/30";
    if (percentage >= 25) return "bg-orange-500/20 border-orange-500/30";
    return "bg-red-500/20 border-red-500/30";
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/outlook-calendar")}
            className="text-slate-400 hover:text-white -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Calendar Sync
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/committee-availability")}
            className="text-yellow-500 hover:text-yellow-400"
          >
            <Users className="w-4 h-4 mr-2" />
            Committee Availability
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Clock className="w-8 h-8 mr-3 text-amber-500" />
            Meeting Time Finder
          </h1>
          <p className="text-slate-400 mt-2">
            Find the best times when committee members are available
          </p>
        </div>

        {/* Search Form */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Duration (minutes)</label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
              >
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={findTimes}
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Find Times
              </Button>
            </div>
          </div>
        </Card>

        {/* Results */}
        {results && (
          <>
            {/* Summary */}
            <Card className="p-4 mb-4 bg-slate-800/50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">
                    {new Date(results.date + "T00:00:00").toLocaleDateString('en-SG', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h2>
                  <p className="text-sm text-slate-400">
                    {results.total_members} committee members • {results.duration_minutes} min meetings
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-500">
                    {results.best_slots.filter(s => s.availability_percentage === 100).length}
                  </p>
                  <p className="text-xs text-slate-400">slots with 100% availability</p>
                </div>
              </div>
            </Card>

            {/* Members */}
            <Card className="p-4 mb-4">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                Committee Members ({results.total_members})
              </h3>
              <div className="flex flex-wrap gap-2">
                {results.members.map((member, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                    {member.name}
                  </span>
                ))}
              </div>
            </Card>

            {/* Best Slots */}
            <h3 className="text-lg font-bold text-white mb-3">Best Time Slots</h3>
            <div className="space-y-3 mb-6">
              {results.best_slots.slice(0, 10).map((slot, idx) => (
                <Card 
                  key={idx} 
                  className={`p-4 border ${getAvailabilityBg(slot.availability_percentage)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${getAvailabilityColor(slot.availability_percentage)} flex items-center justify-center`}>
                        <span className="text-white font-bold text-sm">
                          {Math.round(slot.availability_percentage)}%
                        </span>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-white">
                          {slot.start_time} - {slot.end_time}
                        </p>
                        <p className="text-sm text-slate-400">
                          {slot.available_count}/{slot.total_members} members available
                        </p>
                      </div>
                    </div>
                    
                    {slot.availability_percentage === 100 && (
                      <span className="flex items-center text-green-400 text-sm font-bold">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Everyone Free
                      </span>
                    )}
                  </div>
                  
                  {/* Show who's available/unavailable */}
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-green-400 mb-1 flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Available ({slot.available_count})
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {slot.available_members.slice(0, 5).map((name, i) => (
                            <span key={i} className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded">
                              {name.split(' ')[0]}
                            </span>
                          ))}
                          {slot.available_members.length > 5 && (
                            <span className="text-xs text-slate-400">
                              +{slot.available_members.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {slot.unavailable_members.length > 0 && (
                        <div>
                          <p className="text-xs text-red-400 mb-1 flex items-center">
                            <XCircle className="w-3 h-3 mr-1" />
                            Unavailable ({slot.unavailable_members.length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {slot.unavailable_members.slice(0, 5).map((member, i) => (
                              <span key={i} className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">
                                {member.name.split(' ')[0]}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Full Timeline */}
            <Card className="p-4">
              <h3 className="text-sm font-bold text-white mb-3">Full Day Timeline</h3>
              <div className="overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                  {results.all_slots.map((slot, idx) => (
                    <div
                      key={idx}
                      className={`w-8 h-12 rounded flex flex-col items-center justify-center text-xs cursor-pointer transition-opacity hover:opacity-80 ${getAvailabilityColor(slot.availability_percentage)}`}
                      title={`${slot.start_time}: ${slot.available_count}/${slot.total_members} available`}
                    >
                      <span className="text-white font-bold">{slot.available_count}</span>
                      <span className="text-white/70 text-[10px]">{slot.start_time.split(':')[0]}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>{results.timeline.day_start}</span>
                  <span>{results.timeline.day_end}</span>
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex gap-4 mt-4 justify-center text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-500"></div>
                  100%
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-emerald-500"></div>
                  75%+
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-yellow-500"></div>
                  50%+
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-orange-500"></div>
                  25%+
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-red-500"></div>
                  &lt;25%
                </span>
              </div>
            </Card>
          </>
        )}

        {/* No Results */}
        {!results && !loading && (
          <Card className="p-8 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <h3 className="text-lg font-bold text-white mb-2">Find the Best Meeting Time</h3>
            <p className="text-slate-400">
              Select a date above to see when committee members are available
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
